// Конфигурация для разных типов схем
export interface SchemaTypeConfig {
  name: string;
  allowedDeviceTypes: string[];
  nodeRenderer: 'standard' | 'terminal' | 'knx' | 'rs485';
  backgroundGrid: 'dots' | 'grid' | 'lines';
  tools: string[];
  canvasColor: string;
  electricalPanelRequired?: boolean;
  availablePanels?: boolean;
  
  // NEW: Полная изоляция схем
  dataNamespace: string;           // Namespace для БД (schema_layout_main, schema_layout_terminal)
  nodeTypes: string[];            // Разные компоненты для React Flow
  libraryType: 'devices' | 'cables' | 'terminals' | 'knx' | 'rs485'; // Тип библиотеки элементов
  apiEndpoints: {                 // API эндпоинты (connections общие!)
    layout: string;
    devices: string;
    library: string;
  };
  sharedEndpoints: {              // Общие для всех схем
    connections: string;          // Соединения контактов общие!
  };
  toolHandlers: Record<string, string>; // Обработчики инструментов
  isolatedStorage: boolean;       // Изолированный localStorage
}

export const SCHEMA_CONFIGS: Record<string, SchemaTypeConfig> = {
  main: {
    name: 'Основная схема',
    allowedDeviceTypes: ['light', 'socket', 'switch', 'sensor', 'cable', 'breaker', 'actuator'],
    nodeRenderer: 'standard',
    backgroundGrid: 'dots',
    tools: ['grid-snap', 'print', 'search', 'layers'],
    canvasColor: '#f9fafb',
    electricalPanelRequired: false,
    
    // NEW: Изоляция основной схемы
    dataNamespace: 'main',
    nodeTypes: ['deviceNode', 'cableNode', 'breakerNode'],
    libraryType: 'devices',
    apiEndpoints: {
      layout: '/api/schema_layout',
      devices: '/api/device-instances',
      library: '/api/available-devices'
    },
    sharedEndpoints: {
      connections: '/api/connections'
    },
    toolHandlers: {
      'grid-snap': 'handleGridSnap',
      'search': 'handleMainSearch', 
      'print': 'handleMainPrint',
      'layers': 'handleLayers'
    },
    isolatedStorage: true
  },
  terminal: {
    name: 'Схема клеммников',
    allowedDeviceTypes: ['terminal', 'cable'],
    nodeRenderer: 'terminal',
    backgroundGrid: 'grid',
    tools: ['connect', 'terminal-block', 'jumper', 'print', 'electrical-panel-selector'],
    canvasColor: '#f0f9ff',
    electricalPanelRequired: true,
    availablePanels: true,
    
    // NEW: Изоляция терминальной схемы
    dataNamespace: 'terminal',
    nodeTypes: ['terminalBlockNode', 'terminalCableNode'],
    libraryType: 'cables',
    apiEndpoints: {
      layout: '/api/schema_layout',
      devices: '/api/terminal-instances', 
      library: '/api/available-cables'
    },
    sharedEndpoints: {
      connections: '/api/connections'
    },
    toolHandlers: {
      'connect': 'handleTerminalConnect',
      'terminal-block': 'handleTerminalBlock',
      'jumper': 'handleTerminalJumper',
      'print': 'handleTerminalPrint',
      'electrical-panel-selector': 'handlePanelSelector'
    },
    isolatedStorage: true
  },
  knx: {
    name: 'KNX шина',
    allowedDeviceTypes: ['knx_module', 'knx_sensor', 'knx_actuator', 'knx_line'],
    nodeRenderer: 'standard',
    backgroundGrid: 'dots',
    tools: ['grid-snap', 'print', 'search', 'layers'],
    canvasColor: '#f9fafb',
    electricalPanelRequired: false,
    
    // NEW: Изоляция KNX схемы  
    dataNamespace: 'knx',
    nodeTypes: ['device', 'actuator', 'terminalBlock'],
    libraryType: 'devices',
    apiEndpoints: {
      layout: '/api/schema_layout',
      devices: '/api/device-instances',
      library: '/api/available-devices'
    },
    sharedEndpoints: {
      connections: '/api/connections'
    },
    toolHandlers: {
      'grid-snap': 'handleGridSnap',
      'search': 'handleMainSearch',
      'print': 'handleMainPrint',
      'layers': 'handleLayers'
    },
    isolatedStorage: true
  },
  rs485: {
    name: 'RS-485 сеть',
    allowedDeviceTypes: ['actuator', 'sensor', 'cable', 'device'],
    nodeRenderer: 'standard',
    backgroundGrid: 'dots',
    tools: ['grid-snap', 'print', 'search', 'layers'],
    canvasColor: '#f9fafb',
    electricalPanelRequired: false,
    
    // NEW: Изоляция RS-485 схемы
    dataNamespace: 'rs485',
    nodeTypes: ['device', 'actuator', 'terminalBlock'],
    libraryType: 'devices',
    apiEndpoints: {
      layout: '/api/schema_layout',
      devices: '/api/device-instances',
      library: '/api/available-devices'
    },
    sharedEndpoints: {
      connections: '/api/connections'
    },
    toolHandlers: {
      'grid-snap': 'handleGridSnap',
      'search': 'handleMainSearch',
      'print': 'handleMainPrint',
      'layers': 'handleLayers'
    },
    isolatedStorage: true
  },
  breaker: {
    name: 'Автоматы',
    allowedDeviceTypes: ['breaker', 'circuit_breaker', 'rcd', 'surge_protector'],
    nodeRenderer: 'standard',
    backgroundGrid: 'dots',
    tools: ['grid-snap', 'print', 'search', 'layers', 'electrical-panel-selector'],
    canvasColor: '#f9fafb',
    electricalPanelRequired: true,
    
    // NEW: Изоляция схемы автоматов
    dataNamespace: 'breaker',
    nodeTypes: ['device', 'actuator', 'terminalBlock'],
    libraryType: 'devices',
    apiEndpoints: {
      layout: '/api/schema_layout',
      devices: '/api/device-instances',
      library: '/api/available-devices'
    },
    sharedEndpoints: {
      connections: '/api/connections'
    },
    toolHandlers: {
      'grid-snap': 'handleGridSnap',
      'search': 'handleMainSearch',
      'print': 'handleMainPrint',
      'layers': 'handleLayers',
      'electrical-panel-selector': 'handlePanelSelector'
    },
    isolatedStorage: true
  },
  ethernet: {
    name: 'Ethernet сеть',
    allowedDeviceTypes: ['ethernet_switch', 'ethernet_device', 'ethernet_cable'],
    nodeRenderer: 'standard',
    backgroundGrid: 'dots',
    tools: ['grid-snap', 'print', 'search', 'layers'],
    canvasColor: '#f9fafb',
    electricalPanelRequired: false,
    
    // NEW: Изоляция Ethernet схемы
    dataNamespace: 'ethernet',
    nodeTypes: ['device', 'actuator', 'terminalBlock'],
    libraryType: 'devices',
    apiEndpoints: {
      layout: '/api/schema_layout',
      devices: '/api/device-instances',
      library: '/api/available-devices'
    },
    sharedEndpoints: {
      connections: '/api/connections'
    },
    toolHandlers: {
      'grid-snap': 'handleGridSnap',
      'search': 'handleMainSearch',
      'print': 'handleMainPrint',
      'layers': 'handleLayers'
    },
    isolatedStorage: true
  },
  panel_layout: {
    name: 'Схема щита',
    allowedDeviceTypes: ['device', 'breaker', 'actuator'],
    nodeRenderer: 'standard',
    backgroundGrid: 'dots',
    tools: ['grid-snap', 'print'],
    canvasColor: '#f9fafb',
    electricalPanelRequired: true,
    
    // Изоляция схемы щита
    dataNamespace: 'panel_layout',
    nodeTypes: ['deviceRect', 'panelOutline'],
    libraryType: 'devices',
    apiEndpoints: {
      layout: '/api/schema_layout',
      devices: '/api/device-instances',
      library: '/api/device-instances'
    },
    sharedEndpoints: {
      connections: '/api/connections'
    },
    toolHandlers: {
      'grid-snap': 'handleGridSnap',
      'print': 'handleMainPrint'
    },
    isolatedStorage: true
  },
  floor_plan: {
    name: 'На плане',
    allowedDeviceTypes: ['device', 'actuator', 'sensor', 'switch', 'light', 'socket', 'cable'],
    nodeRenderer: 'standard',
    backgroundGrid: 'dots',
    tools: ['search'],
    canvasColor: '#f9fafb',
    electricalPanelRequired: false,
    dataNamespace: 'floor_plan',
    nodeTypes: ['floorPlanMarker'],
    libraryType: 'devices',
    apiEndpoints: {
      layout: '/api/floor_plan/items',
      devices: '/api/device-instances',
      library: '/api/device-instances'
    },
    sharedEndpoints: {
      connections: '/api/connections'
    },
    toolHandlers: {
      'search': 'handleMainSearch'
    },
    isolatedStorage: true
  },
  network: {
    name: 'Схема сети',
    allowedDeviceTypes: ['device', 'actuator', 'sensor', 'switch', 'light', 'socket', 'cable'],
    nodeRenderer: 'standard',
    backgroundGrid: 'dots',
    tools: ['search'],
    canvasColor: '#f9fafb',
    electricalPanelRequired: false,
    dataNamespace: 'network',
    nodeTypes: ['floorPlanMarker'],
    libraryType: 'devices',
    apiEndpoints: {
      layout: '/api/floor_plan/items',
      devices: '/api/device-instances',
      library: '/api/device-instances'
    },
    sharedEndpoints: {
      connections: '/api/connections'
    },
    toolHandlers: {
      'search': 'handleMainSearch'
    },
    isolatedStorage: true
  },
  wirenboard: {
    name: 'Wirenboard',
    allowedDeviceTypes: ['device', 'actuator', 'sensor', 'switch', 'light', 'socket', 'cable'],
    nodeRenderer: 'standard',
    backgroundGrid: 'dots',
    tools: ['search'],
    canvasColor: '#f9fafb',
    electricalPanelRequired: false,
    dataNamespace: 'wirenboard',
    nodeTypes: ['floorPlanMarker'],
    libraryType: 'devices',
    apiEndpoints: {
      layout: '/api/floor_plan/items',
      devices: '/api/device-instances',
      library: '/api/device-instances'
    },
    sharedEndpoints: {
      connections: '/api/connections'
    },
    toolHandlers: {
      'search': 'handleMainSearch'
    },
    isolatedStorage: true
  }
};

// Utility functions
export const getSchemaConfig = (schemaType: string): SchemaTypeConfig => {
  return SCHEMA_CONFIGS[schemaType] || SCHEMA_CONFIGS.main;
};

export const isValidSchemaType = (schemaType: string): boolean => {
  return schemaType in SCHEMA_CONFIGS;
};

export const getSchemaTypeFromPath = (path: string): string => {
  // Extract schema type from URL patterns like:
  // /projects/1/schemas/main/1 -> main
  // /projects/1/schemas/terminal -> terminal
  // /projects/1/schemas/1/edit -> main (legacy)
  
  const parts = path.split('/');
  const schemasIndex = parts.indexOf('schemas');
  
  if (schemasIndex !== -1 && schemasIndex + 1 < parts.length) {
    const nextPart = parts[schemasIndex + 1];
    
    // Check if it's a valid schema type
    if (isValidSchemaType(nextPart)) {
      return nextPart;
    }
  }
  
  // Default to main for legacy routes
  return 'main';
}; 